/**
 * Portal Storage Service
 * Abstraction layer for localStorage with async pattern for future API migration
 */
export class PortalStorageService {
  constructor(userId) {
    this.userId = userId;
    this.useServerStorage = false; // Flip to true when API is ready
    this.memoryFallback = {}; // Fallback if localStorage is unavailable
    this.storageAvailable = this._checkStorageAvailable();
  }

  _checkStorageAvailable() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  _getKey(name) {
    return `portal_${this.userId}_${name}`;
  }

  _get(key) {
    if (this.storageAvailable) {
      const value = localStorage.getItem(this._getKey(key));
      if (!value) return null;
      try {
        return JSON.parse(value);
      } catch (e) {
        return null;
      }
    }
    return this.memoryFallback[key] || null;
  }

  _set(key, value) {
    if (this.storageAvailable) {
      localStorage.setItem(this._getKey(key), JSON.stringify(value));
    } else {
      this.memoryFallback[key] = value;
    }
  }

  // ============================================
  // Favorites
  // ============================================
  async getFavorites() {
    if (this.useServerStorage) {
      // TODO: API call
      // return await fetch(`/api/portal/favorites`).then(r => r.json());
    }
    return this._get('favorites') || [];
  }

  async addFavorite(appId) {
    if (this.useServerStorage) {
      // TODO: API call
      // return await fetch(`/api/portal/favorites`, { method: 'POST', body: JSON.stringify({ appId }) });
    }
    const favorites = await this.getFavorites();
    if (!favorites.includes(appId)) {
      favorites.push(appId);
      this._set('favorites', favorites);
    }
    return favorites;
  }

  async removeFavorite(appId) {
    if (this.useServerStorage) {
      // TODO: API call
      // return await fetch(`/api/portal/favorites/${appId}`, { method: 'DELETE' });
    }
    let favorites = await this.getFavorites();
    favorites = favorites.filter(id => id !== appId);
    this._set('favorites', favorites);
    return favorites;
  }

  // ============================================
  // Recently Used
  // ============================================
  async getRecentlyUsed() {
    if (this.useServerStorage) {
      // TODO: API call
    }
    return this._get('recently_used') || [];
  }

  async addRecentlyUsed(appId) {
    if (this.useServerStorage) {
      // TODO: API call
    }
    let recentlyUsed = await this.getRecentlyUsed();

    // Remove existing entry for this app
    recentlyUsed = recentlyUsed.filter(item => item.appId !== appId);

    // Add to beginning with timestamp
    recentlyUsed.unshift({
      appId: appId,
      timestamp: Date.now()
    });

    // Limit to 5 entries
    recentlyUsed = recentlyUsed.slice(0, 5);

    this._set('recently_used', recentlyUsed);
    return recentlyUsed;
  }

  // ============================================
  // Section States (collapsed/expanded)
  // ============================================
  async getSectionStates() {
    if (this.useServerStorage) {
      // TODO: API call
    }
    return this._get('section_states') || {
      recentlyUsed: true, // collapsed by default
      favorites: false,
      allApps: false
    };
  }

  async setSectionState(sectionId, collapsed) {
    if (this.useServerStorage) {
      // TODO: API call
    }
    const states = await this.getSectionStates();
    states[sectionId] = collapsed;
    this._set('section_states', states);
    return states;
  }

  // ============================================
  // View Mode
  // ============================================
  async getViewMode() {
    if (this.useServerStorage) {
      // TODO: API call
    }
    return this._get('view_mode') || 'card';
  }

  async setViewMode(mode) {
    if (this.useServerStorage) {
      // TODO: API call
    }
    this._set('view_mode', mode);
    return mode;
  }

  // ============================================
  // Cleanup stale data
  // ============================================
  async cleanupStaleData(validAppIds) {
    // Clean favorites
    let favorites = await this.getFavorites();
    favorites = favorites.filter(id => validAppIds.includes(id));
    this._set('favorites', favorites);

    // Clean recently used
    let recentlyUsed = await this.getRecentlyUsed();
    recentlyUsed = recentlyUsed.filter(item => validAppIds.includes(item.appId));
    this._set('recently_used', recentlyUsed);
  }
}
